import React, { useMemo, PropsWithChildren } from 'react'
import useEventEmitter from 'ahooks/es/useEventEmitter'
import { useDataCollector } from '@/modules/globals/hooks/useDataCollector'
import type { InViewEventContextValue, InViewEventContextProviderProps, InViewEvent } from './typings'

/**
 * 创建 `InView` 事件上下文，用于收集和处理 `InView` 事件
 *
 * 通过统一的类型定义，提供 `InView` 事件的上下文提供者和上下文使用者，方便在列表和表格场景中批量处理内部元素的 `InView` 事件
 * 接入参考：`src/components/AntdBaseTable/components/RowInView/index.tsx`
 */
export function createInViewEventContext() {
    const context = React.createContext<InViewEventContextValue | null>(null)

    /** `InView` 事件上下文提供者 */
    function Provider<D = unknown>(props: PropsWithChildren<InViewEventContextProviderProps<D>>): React.ReactElement {
        const { options, onVisibleChange, children } = props

        const { Provider } = context as React.Context<InViewEventContextValue<D>>
        const { collect } = useDataCollector({ maxCount: 10, interval: 500, onIntervalEnd: onVisibleChange })
        const event$ = useEventEmitter<InViewEvent<D>>()

        event$.useSubscription(event => {
            if (!event.isInView) {
                return
            }

            collect(event.data)
        })

        const contextValue = useMemo<InViewEventContextValue<D>>(() => {
            return { event$, options }
        }, [event$, options])

        return <Provider value={contextValue}>{children}</Provider>
    }

    /** 使用 `InView` 事件上下文 */
    function useContext<D = unknown>(): InViewEventContextValue<D> {
        const contextValue = React.useContext(context as React.Context<InViewEventContextValue<D>>)
        if (!contextValue) {
            throw new Error('useInViewEventContext must be used in InViewEventContextProvider')
        }
        return contextValue
    }

    return { Provider, useContext } as const
}

export * from './typings'
